/**
 * Access-token store per phase1-fe-be-contract.md §4.2.
 *
 * refreshToken lives in HttpOnly Secure cookie (BE-set); accessToken is
 * persisted to sessionStorage so page-refresh within the same tab doesn't
 * force re-login. Tab close clears sessionStorage automatically.
 * Backlog: migrate to HttpOnly cookie for the access token too.
 */
export interface AuthClaims {
  sub: string;
  roles: readonly string[];
  twoFactorRequired: boolean;
  exp: number;
  [k: string]: unknown;
}

type Listener = (token: string | null) => void;

const SESSION_KEY = 'everline-backoffice-token';

/**
 * Session-history flag. Set when a login first establishes an access token and
 * kept across in-tab page refreshes (sessionStorage). It lets the 401 handler
 * distinguish a logged-in user whose access token merely expired (→ refresh)
 * from an anonymous visitor who never authenticated (→ skip refresh, no noise).
 * Cleared on logout / terminal auth loss. See C-1 (anonymous refresh 401 noise).
 */
const SESSION_FLAG_KEY = 'everline-backoffice-session';

function readFromStorage(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(SESSION_KEY);
  } catch {
    return null;
  }
}

function writeToStorage(token: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (token) {
      sessionStorage.setItem(SESSION_KEY, token);
    } else {
      sessionStorage.removeItem(SESSION_KEY);
    }
  } catch {
    // storage quota exceeded or blocked — degrade gracefully
  }
}

function readSessionFlag(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(SESSION_FLAG_KEY) === '1';
  } catch {
    return false;
  }
}

function writeSessionFlag(hadSession: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    if (hadSession) {
      sessionStorage.setItem(SESSION_FLAG_KEY, '1');
    } else {
      sessionStorage.removeItem(SESSION_FLAG_KEY);
    }
  } catch {
    // degrade gracefully
  }
}

class TokenStore {
  #token: string | null = readFromStorage();
  #claims: AuthClaims | null = null;
  #hadSession = readSessionFlag() || this.#token !== null;
  #listeners = new Set<Listener>();
  #refreshInFlight: Promise<string | null> | null = null;
  #authLostNotified = false;

  get token(): string | null {
    return this.#token;
  }

  get claims(): AuthClaims | null {
    return this.#claims;
  }

  /**
   * True once a session has been established in this tab (and not yet cleared).
   * Survives in-tab page refreshes so a logged-in user whose access token was
   * dropped from memory still attempts a silent token refresh, while a never-
   * authenticated visitor does not. Drives C-1 (skip refresh for anonymous).
   */
  get hadSession(): boolean {
    return this.#hadSession;
  }

  set(token: string | null, claims: AuthClaims | null = null): void {
    this.#token = token;
    this.#claims = claims;
    writeToStorage(token);
    if (token) {
      this.#hadSession = true;
      writeSessionFlag(true);
      // a fresh token re-arms the one-shot auth-lost guard for the next session
      this.#authLostNotified = false;
    }
    for (const l of this.#listeners) l(token);
  }

  clear(): void {
    this.#hadSession = false;
    writeSessionFlag(false);
    this.set(null, null);
  }

  subscribe(l: Listener): () => void {
    this.#listeners.add(l);
    return () => this.#listeners.delete(l);
  }

  /** Single-flight guard: concurrent 401 retries reuse the same refresh promise. */
  async withRefreshLock(fn: () => Promise<string | null>): Promise<string | null> {
    if (this.#refreshInFlight) return this.#refreshInFlight;
    this.#refreshInFlight = fn().finally(() => {
      this.#refreshInFlight = null;
    });
    return this.#refreshInFlight;
  }

  /**
   * One-shot terminal auth-loss latch. Concurrent 401s would each fire
   * `onAuthLost` and race competing redirects; this returns true only for the
   * first caller after a session was active. Re-armed when a new token is set.
   */
  markAuthLostOnce(): boolean {
    if (this.#authLostNotified) return false;
    this.#authLostNotified = true;
    return true;
  }
}

export const tokenStore = new TokenStore();
